import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Receipt,
  FileText,
  Calendar,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Percent,
  Calculator,
  PieChart,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Building,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { formatCurrency, formatDate } from '../lib/utils';

// Dummy data for financial records
const dummyTransactions = [
  {
    id: 'TXN-001',
    type: 'sale',
    amount: 127.50,
    fee: 3.95,
    netAmount: 123.55,
    currency: 'USD',
    paymentMethod: 'stripe',
    status: 'completed',
    customerName: 'Sarah Johnson',
    orderId: 'ORD-001',
    description: 'Blue Mountain Coffee - Premium Blend',
    processedAt: new Date('2024-01-25T14:30:00'),
    settledAt: new Date('2024-01-26T09:00:00'),
  },
  {
    id: 'TXN-002',
    type: 'refund',
    amount: -45.00,
    fee: -1.35,
    netAmount: -43.65,
    currency: 'USD',
    paymentMethod: 'stripe',
    status: 'completed',
    customerName: 'Michael Chen',
    orderId: 'ORD-002',
    description: 'Refund - Jerk Seasoning Blend',
    processedAt: new Date('2024-01-24T11:15:00'),
    settledAt: new Date('2024-01-25T09:00:00'),
  },
  {
    id: 'TXN-003',
    type: 'sale',
    amount: 89.99,
    fee: 2.87,
    netAmount: 87.12,
    currency: 'USD',
    paymentMethod: 'paypal',
    status: 'pending',
    customerName: 'Emily Rodriguez',
    orderId: 'ORD-003',
    description: 'Coconut Oil & Spice Bundle',
    processedAt: new Date('2024-01-25T16:45:00'),
    settledAt: null,
  },
  {
    id: 'TXN-004',
    type: 'sale',
    amount: 234.75,
    fee: 7.12,
    netAmount: 227.63,
    currency: 'USD',
    paymentMethod: 'stripe',
    status: 'failed',
    customerName: 'David Thompson',
    orderId: 'ORD-004',
    description: 'Coffee Gift Set - Large',
    processedAt: new Date('2024-01-25T18:20:00'),
    settledAt: null,
  },
];

const dummyRevenueData = [
  { period: 'Jan 2024', revenue: 45678.90, expenses: 12345.67, profit: 33333.23, orders: 234 },
  { period: 'Dec 2023', revenue: 52341.45, expenses: 15678.90, profit: 36662.55, orders: 267 },
  { period: 'Nov 2023', revenue: 48923.12, expenses: 13456.78, profit: 35466.34, orders: 245 },
  { period: 'Oct 2023', revenue: 41567.89, expenses: 11234.56, profit: 30333.33, orders: 198 },
];

const dummyTaxRecords = [
  {
    id: 'TAX-001',
    period: 'Q4 2023',
    totalSales: 187832.46,
    taxableAmount: 187832.46,
    taxRate: 8.25,
    taxCollected: 15495.18,
    taxRemitted: 15495.18,
    status: 'filed',
    dueDate: new Date('2024-01-31'),
    filedDate: new Date('2024-01-28'),
  },
  {
    id: 'TAX-002',
    period: 'Q3 2023',
    totalSales: 156789.23,
    taxableAmount: 156789.23,
    taxRate: 8.25,
    taxCollected: 12935.11,
    taxRemitted: 12935.11,
    status: 'filed',
    dueDate: new Date('2023-10-31'),
    filedDate: new Date('2023-10-29'),
  },
  {
    id: 'TAX-003',
    period: 'Q1 2024',
    totalSales: 45678.90,
    taxableAmount: 45678.90,
    taxRate: 8.25,
    taxCollected: 3768.51,
    taxRemitted: 0,
    status: 'pending',
    dueDate: new Date('2024-04-30'),
    filedDate: null,
  },
];

const dummyExpenses = [
  {
    id: 'EXP-001',
    category: 'Inventory',
    description: 'Coffee beans wholesale purchase',
    amount: 2450.00,
    vendor: 'Blue Mountain Farms',
    date: new Date('2024-01-20'),
    status: 'paid',
    receiptUrl: '/receipts/exp-001.pdf',
  },
  {
    id: 'EXP-002',
    category: 'Shipping',
    description: 'Monthly shipping costs - FedEx',
    amount: 1234.56,
    vendor: 'FedEx',
    date: new Date('2024-01-15'),
    status: 'paid',
    receiptUrl: '/receipts/exp-002.pdf',
  },
  {
    id: 'EXP-003',
    category: 'Marketing',
    description: 'Google Ads campaign',
    amount: 567.89,
    vendor: 'Google',
    date: new Date('2024-01-22'),
    status: 'pending',
    receiptUrl: null,
  },
  {
    id: 'EXP-004',
    category: 'Software',
    description: 'Stripe processing fees',
    amount: 234.12,
    vendor: 'Stripe',
    date: new Date('2024-01-25'),
    status: 'paid',
    receiptUrl: '/receipts/exp-004.pdf',
  },
];

export function Financial() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const getTransactionStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { color: 'bg-green-100 text-green-800', label: 'Completed' };
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' };
      case 'failed':
        return { color: 'bg-red-100 text-red-800', label: 'Failed' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  const getTransactionTypeConfig = (type: string) => {
    switch (type) {
      case 'sale':
        return { color: 'bg-green-100 text-green-800', label: 'Sale', icon: ArrowUpRight };
      case 'refund':
        return { color: 'bg-red-100 text-red-800', label: 'Refund', icon: ArrowDownRight };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown', icon: DollarSign };
    }
  };

  const getTaxStatusConfig = (status: string) => {
    switch (status) {
      case 'filed':
        return { color: 'bg-green-100 text-green-800', label: 'Filed' };
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' };
      case 'overdue':
        return { color: 'bg-red-100 text-red-800', label: 'Overdue' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  // Calculate stats
  const totalRevenue = dummyTransactions
    .filter(t => t.type === 'sale' && t.status === 'completed')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalRefunds = Math.abs(dummyTransactions
    .filter(t => t.type === 'refund')
    .reduce((sum, t) => sum + t.amount, 0));
  const pendingAmount = dummyTransactions
    .filter(t => t.status === 'pending')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalFees = dummyTransactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + t.fee, 0);

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Revenue Chart Placeholder */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-jamaica-green" />
            Revenue Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Revenue chart will be implemented with Recharts</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Revenue Data */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-jamaica-green" />
            Monthly Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Period</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Expenses</TableHead>
                  <TableHead>Profit</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyRevenueData.map((data, index) => {
                  const margin = ((data.profit / data.revenue) * 100);
                  
                  return (
                    <TableRow
                      key={data.period}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell className="font-medium">{data.period}</TableCell>
                      <TableCell className="font-semibold text-green-600">
                        {formatCurrency(data.revenue)}
                      </TableCell>
                      <TableCell className="text-red-600">
                        {formatCurrency(data.expenses)}
                      </TableCell>
                      <TableCell className="font-semibold text-jamaica-green">
                        {formatCurrency(data.profit)}
                      </TableCell>
                      <TableCell>{data.orders}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="font-medium">{margin.toFixed(1)}%</span>
                          {margin > 70 ? (
                            <TrendingUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTransactionsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-jamaica-green" />
            Payment Transactions ({dummyTransactions.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Transaction</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Fees</TableHead>
                  <TableHead>Net</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyTransactions.map((transaction, index) => {
                  const statusConfig = getTransactionStatusConfig(transaction.status);
                  const typeConfig = getTransactionTypeConfig(transaction.type);
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <TableRow
                      key={transaction.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-gray-900">{transaction.id}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{transaction.description}</p>
                          <p className="text-xs text-gray-400">Order: {transaction.orderId}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-gray-900">{transaction.customerName}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeConfig.color}>
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className={`font-semibold ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(Math.abs(transaction.amount))}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(Math.abs(transaction.fee))}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className={`font-semibold ${transaction.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(Math.abs(transaction.netAmount))}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">{formatDate(transaction.processedAt)}</p>
                          {transaction.settledAt && (
                            <p className="text-xs text-gray-500">
                              Settled: {formatDate(transaction.settledAt)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Download Receipt
                            </DropdownMenuItem>
                            {transaction.status === 'failed' && (
                              <DropdownMenuItem>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Retry Payment
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTaxTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-jamaica-green" />
            Tax Records ({dummyTaxRecords.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Period</TableHead>
                  <TableHead>Total Sales</TableHead>
                  <TableHead>Tax Rate</TableHead>
                  <TableHead>Tax Collected</TableHead>
                  <TableHead>Tax Remitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyTaxRecords.map((tax, index) => {
                  const statusConfig = getTaxStatusConfig(tax.status);
                  
                  return (
                    <TableRow
                      key={tax.id}
                      className="hover:bg-gray-50 transition-colors"
                      style={{
                        opacity: 0,
                        transform: 'translateX(-20px)',
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                      }}
                    >
                      <TableCell className="font-medium">{tax.period}</TableCell>
                      <TableCell className="font-semibold text-gray-900">
                        {formatCurrency(tax.totalSales)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Percent className="w-3 h-3 text-gray-400" />
                          <span>{tax.taxRate}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-jamaica-green">
                        {formatCurrency(tax.taxCollected)}
                      </TableCell>
                      <TableCell className="font-semibold text-blue-600">
                        {formatCurrency(tax.taxRemitted)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm text-gray-600">{formatDate(tax.dueDate)}</p>
                          {tax.filedDate && (
                            <p className="text-xs text-green-600">
                              Filed: {formatDate(tax.filedDate)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              View Report
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {tax.status === 'pending' && (
                              <DropdownMenuItem>
                                <FileText className="w-4 h-4 mr-2" />
                                File Return
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderExpensesTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-jamaica-green" />
            Business Expenses ({dummyExpenses.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dummyExpenses.map((expense, index) => (
                  <TableRow
                    key={expense.id}
                    className="hover:bg-gray-50 transition-colors"
                    style={{
                      opacity: 0,
                      transform: 'translateX(-20px)',
                      animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s forwards`
                    }}
                  >
                    <TableCell>
                      <p className="font-medium text-gray-900">{expense.description}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{expense.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600">{expense.vendor}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-red-600">
                        {formatCurrency(expense.amount)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600">{formatDate(expense.date)}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={expense.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {expense.receiptUrl ? (
                        <Button variant="ghost" size="sm">
                          <FileText className="w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-gray-400 text-sm">No receipt</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Expense
                          </DropdownMenuItem>
                          {expense.receiptUrl && (
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Download Receipt
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Expense
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes fadeInSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Action Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex justify-end"
        >
          <Button className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Generate Report
          </Button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <Card className="admin-card">
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="flex items-center mt-auto pt-4 text-sm">
                <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">+12.5% from last month</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Processing Fees</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(totalFees)}</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="flex items-center mt-auto pt-4 text-sm">
                <span className="text-gray-600">2.8% avg fee rate</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Settlements</p>
                  <p className="text-2xl font-bold text-jamaica-yellow">{formatCurrency(pendingAmount)}</p>
                </div>
                <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-jamaica-yellow" />
                </div>
              </div>
              <div className="flex items-center mt-auto pt-4 text-sm">
                <span className="text-gray-600">1-2 business days</span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Refunds Issued</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRefunds)}</p>
                </div>
                <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <ArrowDownRight className="h-6 w-6 text-gray-600" />
                </div>
              </div>
              <div className="flex items-center mt-auto pt-4 text-sm">
                <span className="text-gray-600">1.2% refund rate</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Financial Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white rounded-xl shadow-md border border-gray-100"
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'transactions', label: 'Transactions', icon: CreditCard },
                { id: 'tax', label: 'Tax Records', icon: Calculator },
                { id: 'expenses', label: 'Expenses', icon: Receipt },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === id
                      ? 'border-jamaica-green text-jamaica-green'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'transactions' && renderTransactionsTab()}
            {activeTab === 'tax' && renderTaxTab()}
            {activeTab === 'expenses' && renderExpensesTab()}
          </div>
        </motion.div>
      </div>
    </>
  );
} 